import { Link } from 'react-router-dom';
import { useAccount } from '../../lib/api/hooks';
import { useAppVersion } from '../../lib/useAppVersion';
import { shouldBlockStart } from '../../lib/staleGate';
import { StaleGate } from './StaleGate';
import { useWorkflows } from '../../lib/workflows/useWorkflows';
import type { WorkflowConfig } from '../../lib/workflows/config';
import { isWorkflowAuthorized } from '../../lib/workflows/authorization';
import { useSupport } from '../support/SupportWidget';

/** Provisioning-gated workflows: hidden until a Site Admin enables them for the account. */
// Gating comes from each workflow's own requiresAuthorization flag — see lib/workflows/authorization.

export function NewTransaction() {
  const { openSupport } = useSupport();
  const account = useAccount();
  const workflows = useWorkflows();
  const flags = account.data;

  const isVisible = (w: WorkflowConfig): boolean => isWorkflowAuthorized(flags, w);
  /*
   * Nothing is ever part-way through on this page, so a stale bundle is stopped here outright rather than
   * being allowed to pick a workflow and discover the problem three steps later.
   */
  const { stale } = useAppVersion();

  const all = (workflows.data ?? []).filter((w) => w.enabled);
  const live = all.filter((w) => !w.tabs.includes('documents') && isVisible(w));
  const conversion = all.filter((w) => w.tabs.includes('documents') && isVisible(w));
  const anyGatedHidden = all.some((w) => w.requiresAuthorization && !isVisible(w));

  if (shouldBlockStart(stale, false)) return <StaleGate />;

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>New transaction</h1>
          <div className="sub">Choose a workflow to begin capturing or converting fingerprints.</div>
        </div>
      </div>

      {workflows.isLoading ? (
        <p className="loading-screen">// loading workflows…</p>
      ) : (
        <>
          {live.length > 0 && (
            <>
              <div className="section-title">Live capture</div>
              <div className="wf-picker">{live.map((w) => <WorkflowCard key={w.captureType} wf={w} />)}</div>
            </>
          )}
          {conversion.length > 0 && (
            <>
              <div className="section-title">Card conversion</div>
              <div className="wf-picker">{conversion.map((w) => <WorkflowCard key={w.captureType} wf={w} />)}</div>
            </>
          )}
          {anyGatedHidden && (
            <p className="cart-note" style={{ textAlign: 'left', marginTop: 24 }}>
              // Need SWFT or FBI (DOCE) transactions? Those workflows are provisioned per account —{' '}
              <button type="button" className="linklike" onClick={openSupport}>ask the assistant</button> to enable them.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function WorkflowCard({ wf }: { wf: WorkflowConfig }) {
  return (
    <Link to={`/app/capture/${wf.captureType}`} className="wf-pick-card">
      <div className="wf-pick-top">
        <span className="wf-tag">{wf.tag}</span>
        {wf.requiresDeviceService ? <span className="pill-scan">Scanner</span> : <span className="pill-doc">Upload</span>}
      </div>
      <h3>{wf.label}</h3>
      <p>{wf.specification} · {wf.tot}</p>
      <span className="wf-pick-go">Start →</span>
    </Link>
  );
}
